import { useState, type FormEvent } from 'react'

import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorText,
  Input,
  NumberStepper,
  PageSpinner,
  Select,
  Sheet,
  useToast,
} from '@/components/ui'
import { formatDateTime } from '@/lib/format'

import { useAddPressure, useDeletePressure, usePressure, type BloodPressure } from './api'
import { fieldError, localToIso, otherError, pressureSeries } from './logic'
import { DeleteButton, TabToolbar } from './parts'

const FIELDS = ['systolic', 'diastolic', 'pulse']

function PressureForm({ last, onDone }: { last: BloodPressure | undefined; onDone: () => void }) {
  const toast = useToast()
  const add = useAddPressure()
  const [systolic, setSystolic] = useState<number | null>(last?.systolic ?? 120)
  const [diastolic, setDiastolic] = useState<number | null>(last?.diastolic ?? 80)
  const [pulse, setPulse] = useState<number | null>(null)
  const [arm, setArm] = useState('')
  const [when, setWhen] = useState('')
  const [note, setNote] = useState('')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    add.mutate(
      {
        systolic,
        diastolic,
        pulse,
        arm: arm === 'left' || arm === 'right' ? arm : null,
        note: note.trim() || null,
        measured_at: localToIso(when),
      },
      {
        onSuccess: (saved) => {
          toast.show({
            message: saved.above_normal ? 'Записано: давление выше нормы' : 'Давление записано',
            tone: saved.above_normal ? 'danger' : 'accent',
          })
          onDone()
        },
      },
    )
  }

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <NumberStepper
          label="Верхнее (систолическое)"
          value={systolic}
          onChange={setSystolic}
          min={50}
          max={260}
          error={fieldError(add.error, 'systolic')}
        />
        <NumberStepper
          label="Нижнее (диастолическое)"
          value={diastolic}
          onChange={setDiastolic}
          min={50}
          max={260}
          error={fieldError(add.error, 'diastolic')}
        />
      </div>
      <NumberStepper
        label="Пульс"
        unit="уд/мин"
        value={pulse}
        onChange={setPulse}
        min={20}
        max={250}
        error={fieldError(add.error, 'pulse')}
      />
      <Select
        label="Рука"
        value={arm}
        onChange={(e) => setArm(e.target.value)}
        placeholder="Не указана"
        options={[
          { value: 'left', label: 'Левая' },
          { value: 'right', label: 'Правая' },
        ]}
      />
      <Input
        label="Когда"
        type="datetime-local"
        value={when}
        onChange={(e) => setWhen(e.target.value)}
        hint="Пусто — сейчас"
      />
      <Input label="Заметка" value={note} onChange={(e) => setNote(e.target.value)} />
      <ErrorText error={otherError(add.error, ...FIELDS)} />
      <Button type="submit" variant="primary" loading={add.isPending}>
        Записать
      </Button>
    </form>
  )
}

export function PressurePage() {
  const query = usePressure()
  const remove = useDeletePressure()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const items = query.data ?? []

  if (query.isPending) return <PageSpinner />
  if (query.isError) return <ErrorText error={query.error} />

  const addButton = (
    <Button variant="primary" icon="plus" onClick={() => setOpen(true)}>
      Записать давление
    </Button>
  )

  return (
    <div>
      <TabToolbar description="Верхнее, нижнее и пульс. От 140/90 и выше — выше нормы." action={items.length ? addButton : null} />
      {items.length === 0 ? (
        <Card>
          <EmptyState
            icon="activity"
            title="Давление ещё не записано"
            description="Внеси первый замер — здесь появится график и пометки о значениях выше нормы."
            action={addButton}
          />
        </Card>
      ) : (
        <div className="grid gap-4">
          <Card title="Давление" subtitle="Пунктир — граница нормы, 140 мм рт. ст.">
            <TimeSeriesChart
              series={pressureSeries(items)}
              goal={{ value: 140, label: 'Выше нормы: 140' }}
              defaultPeriod="3m"
            />
          </Card>
          <Card title="Замеры" flush>
            <ul className="divide-y divide-border">
              {items.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`text-lg font-semibold tabular-nums ${p.above_normal ? 'text-danger' : 'text-fg'}`}
                      >
                        {p.systolic}/{p.diastolic}
                      </span>
                      {p.pulse !== null ? <span className="text-sm text-muted">пульс {p.pulse}</span> : null}
                      {p.above_normal ? <Badge tone="danger">выше нормы</Badge> : null}
                    </div>
                    <div className="text-xs text-muted">
                      {formatDateTime(p.measured_at)}
                      {p.arm ? ` · ${p.arm === 'left' ? 'левая рука' : 'правая рука'}` : ''}
                      {p.note ? ` · ${p.note}` : ''}
                    </div>
                  </div>
                  <DeleteButton
                    what={`давление ${p.systolic}/${p.diastolic}`}
                    pending={remove.isPending}
                    error={remove.error}
                    onConfirm={() =>
                      remove.mutateAsync(p.id).then(() => toast.show({ message: 'Замер удалён' }))
                    }
                  />
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
      <Sheet open={open} onClose={() => setOpen(false)} title="Давление">
        <PressureForm last={items[0]} onDone={() => setOpen(false)} />
      </Sheet>
    </div>
  )
}
